import { GeneratedExamStatus, IExam, IQuestion } from '../types/examgenerator';
import { PromiseExtended, Table } from 'dexie';
import { BaseModelForPush } from './base-model';
import { SyncChanges, SyncRecord, SyncRecordStatus } from '../types/sync-state';
import { IEgSupportedSubject } from '../types/user-generated-exam';

const SCHEMA_VERSION = 4;
const SCHEMA = {
  questions: '++id, bookChapter, syncStatus',
  exams: 'id, gradeSubject, type, schoolClass, checkpoint, syncStatus',
  egSupportedSubjects: 'code',
};

export interface QuestionRecord extends IQuestion, SyncRecord {}
export interface GeneratedExamRecord extends IExam, SyncRecord {}

export class ExamGeneratorDB extends BaseModelForPush {
  questions!: Table<QuestionRecord>;
  examGenerator!: Table<GeneratedExamRecord>;
  egSupportedSubjects!: Table<IEgSupportedSubject>;

  constructor() {
    super('examgeneratordb');
    this.version(SCHEMA_VERSION).stores(SCHEMA);

    this.questions = this.table('questions');
    this.examGenerator = this.table('exams');
    this.egSupportedSubjects = this.table('egSupportedSubjects');

    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  getSupportedSubjects(): PromiseExtended<IEgSupportedSubject[]> {
    return this.egSupportedSubjects.toArray();
  }

  getExamById(
    examId: string,
  ): PromiseExtended<GeneratedExamRecord | undefined> {
    return this.examGenerator.get(examId);
  }

  getLatestExamByCheckpointClassAndGradeSubject(
    checkpoint: number,
    schoolClass: number,
    gradeSubject: number,
  ): PromiseExtended<GeneratedExamRecord | undefined> {
    return this.examGenerator
      .where({ checkpoint, schoolClass, gradeSubject })
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('created')
      .then((records) => records.pop());
  }

  getQuestionsByIds(questionIds: number[]): PromiseExtended<QuestionRecord[]> {
    return this.questions
      .where('id')
      .anyOf(questionIds)
      .and((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('bookChapter');
  }

  getExamsByClassAndSubject(
    schoolClass: number,
    gradeSubject: number,
  ): PromiseExtended<GeneratedExamRecord[]> {
    return this.examGenerator
      .where({ schoolClass, gradeSubject })
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  getExamsByClassSubjectAndCheckpoint(
    schoolClass: number,
    gradeSubject: number,
    checkpoint: number,
  ): PromiseExtended<GeneratedExamRecord[]> {
    return this.examGenerator
      .where({ schoolClass, gradeSubject, checkpoint })
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  getExamsByClassAndCheckpoint(
    schoolClass: number,
    checkpoint: number,
  ): PromiseExtended<GeneratedExamRecord[]> {
    return this.examGenerator
      .where({ schoolClass, checkpoint })
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .toArray();
  }

  getQuestionsByChapterIds(
    chapterIds: number[],
  ): PromiseExtended<QuestionRecord[]> {
    return this.questions
      .where('bookChapter')
      .anyOf(chapterIds)
      .and((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('bookChapter');
  }

  getAllExams(): PromiseExtended<GeneratedExamRecord[]> {
    return this.examGenerator
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  setExamStatus(examId: string, status: GeneratedExamStatus): Promise<number> {
    return this.examGenerator.update(examId, { status });
  }

  setExamData(
    examId: string,
    data: Partial<GeneratedExamRecord>,
  ): Promise<number> {
    return this.examGenerator.update(examId, data);
  }

  async isExamSynced(examId: string): Promise<boolean> {
    // any other status other than "createdButNotSynced" means it has been synced and backend has the data
    const record = await this.examGenerator.get(examId);
    return record?.syncStatus !== SyncRecordStatus.CreatedButNotSynced;
  }

  async getUpdatedExams(): Promise<GeneratedExamRecord[]> {
    const records = await this.examGenerator.toArray();
    return records.filter(
      (data) => data.syncStatus === SyncRecordStatus.Updated,
    );
  }

  async addRecordExamGenerator(examData: IExam): Promise<void> {
    try {
      const examExist = (await this.getAllExams()).find(
        (exam) => exam.id === examData.id,
      );
      if (examExist) {
        this.updateExam(examData);
      } else {
        await this.examGenerator.add({
          ...examData,
          created: new Date().toISOString(),
          syncStatus: SyncRecordStatus.CreatedButNotSynced,
        } as GeneratedExamRecord);
      }
    } catch (error) {
      console.error('Error adding record to generated exams', error);
    }
  }

  async updateExam(examData: IExam) {
    try {
      await this.examGenerator.update(examData.id, {
        ...examData,
      });
    } catch (error) {
      console.error('Error updating exam data', error);
    }
  }

  deleteExam(id: string): Promise<number> {
    return this.examGenerator
      .where({ id: id })
      .modify({ syncStatus: SyncRecordStatus.Deleted });
  }

  async getNewCreatedExams(): Promise<GeneratedExamRecord[]> {
    const records = await this.getAllExams();
    const data = records.filter(
      (data) => data.syncStatus === SyncRecordStatus.CreatedButNotSynced,
    );
    data.map((data) => {
      data.syncStatus = SyncRecordStatus.Created;
    });
    return data;
  }

  private async getExamsSyncData(): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await this.examGenerator
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Deleted, SyncRecordStatus.Updated])
      .filter(
        (record) => record.syncStatus !== SyncRecordStatus.CreatedButNotSynced,
      )
      .toArray();
    return records;
  }

  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const examGenerator = [
      ...(await this.getExamsSyncData()),
      ...(await this.getNewCreatedExams()),
    ];
    return { examGenerator };
  }

  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;

    const idsToMarkSynced = {
      examGenerator: syncChanges.examGenerator?.created.map(
        (record: { id: string }) => record.id,
      ),
    };

    for (const [model, ids] of Object.entries(idsToMarkSynced)) {
      if (ids && ids.length > 0) {
        try {
          updates += await this._updateSyncData(
            // @ts-ignore
            this[model],
            ids,
            status,
          );
        } catch (error) {
          console.error(`Failed to update sync status for $te{model}`, error);
        }
      }
    }
    return updates;
  }
}

export const examGeneratorDB = new ExamGeneratorDB();
